import { useParams } from 'react-router';
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, CalendarDays } from 'lucide-react';
import { useGetVerificationQuery } from '@/store/api/apiSlice';
import Logo from '@/assets/newlogo.svg';

function formatMemberSince(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-NG', { month: 'long', year: 'numeric' });
}

export default function Verify() {
    const { id } = useParams<{ id: string }>();
    const { data, isLoading, isError, error } = useGetVerificationQuery(id ?? '', { skip: !id });

    // A 404 (or any failure) means the code does not map to a verifiable member.
    const notAuthentic = isError || (!isLoading && !data);
    const status = (error && 'status' in error ? error.status : undefined) as number | undefined;
    const isNotFound = status === 404;

    return (
        <div className="min-h-dvh bg-[#f1f3f6] flex flex-col items-center px-4 py-10">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="YourChecka" className="h-9 w-auto" />
                </div>

                {isLoading ? (
                    <div className="bg-white rounded-2xl shadow-sm p-10 flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
                        <p className="text-sm text-[#4e4d4d]">Verifying credential…</p>
                    </div>
                ) : notAuthentic ? (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <div className="bg-red-600 px-6 py-8 flex flex-col items-center text-center">
                            <ShieldAlert className="w-12 h-12 text-white mb-3" />
                            <h1 className="text-xl font-bold text-white">Not a recognized member</h1>
                            <p className="text-sm text-red-50 mt-1">Do not proceed</p>
                        </div>
                        <div className="px-6 py-6 text-center">
                            <p className="text-sm text-[#4e4d4d]">
                                {isNotFound
                                    ? 'This code does not match any authorized YourChecka field member. If someone presented it to you as identification, treat it as invalid.'
                                    : 'We could not verify this credential right now. Check your connection and try again.'}
                            </p>
                        </div>
                    </div>
                ) : data ? (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        {/* Authentic banner */}
                        <div className="bg-gradient-to-br from-green-600 to-green-700 px-6 py-8 flex flex-col items-center text-center">
                            <ShieldCheck className="w-12 h-12 text-white mb-3" />
                            <h1 className="text-xl font-bold text-white">Authentic member</h1>
                            <p className="text-sm text-green-50 mt-1">Verified YourChecka platform member</p>
                        </div>

                        <div className="px-6 py-6 flex flex-col gap-5">
                            <div className="text-center">
                                <h2 className="text-2xl font-bold text-[#1b1b1b]">{data.fullName}</h2>
                                <p className="text-sm text-[#FF6B35] font-medium mt-0.5">{data.roleLabel}</p>
                            </div>

                            {/* Active / suspended — tells the verifier they're currently authorized */}
                            <div className="flex justify-center">
                                {data.isActive ? (
                                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 bg-green-50 border border-green-100 px-4 py-1.5 rounded-full">
                                        <CheckCircle2 className="w-4 h-4" /> Active
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-700 bg-red-50 border border-red-100 px-4 py-1.5 rounded-full">
                                        <XCircle className="w-4 h-4" /> Suspended / Inactive
                                    </span>
                                )}
                            </div>

                            <div className="border-t border-[#eee] pt-4 flex items-center justify-center gap-2 text-sm text-[#4e4d4d]">
                                <CalendarDays className="w-4 h-4 text-[#9a9a9a]" />
                                Member since {formatMemberSince(data.memberSince)}
                            </div>

                            {!data.isActive && (
                                <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 text-xs text-red-700 text-center">
                                    This member is not currently active. They are not authorized to act on behalf of
                                    YourChecka at this time.
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                <p className="text-center text-xs text-[#9a9a9a] mt-6">
                    This page confirms a member is registered and currently authorized on the YourChecka platform.
                </p>
            </div>
        </div>
    );
}
